/**
 * 63.14 2b / DIRECTION §0.11 step B (2), the site score (ARCHITECTURE §5.22 "Sites: a footprint's slope and roughness";
 * DECISIONS 205: "prefer flatter land but terrace when necessary"): what a candidate site is worth for a footprint —
 * **flatness first** (the natural rise under the footprint, people), **then the earthwork** (Σ |level − natural| over the
 * samples at the median level: what a terrace there would move), and the **material** (rock and ice refuse a cut: a
 * footprint whose level lies under the hardest ground's crest by more than the seat's blend is marked down hard). Higher is
 * better; 0 is flat ground that moves nothing. Lane C's `SiteScore` (core/openLayout.ts, step A's open board:
 * `(state, planetIndex, dir) => number`) by closing over the footprint — `terraceSiteScore(footprint)` plugs in where
 * `flatSiteScore` does — while `siteReport` is the richer read beside it: the rise, the level, the earthwork, the material,
 * and the pads the site would split into when its rise exceeds the terrace's step cap (two, along the contour: the pass
 * that lays split pads is later; this says what it would lay). Pure; reads the fine ground as the terrace pass does.
 */
import type { GameState, Vec3 } from '../types';
import type { GroundMaterial } from '../types/ground';
import { TERRACE } from './terraceGround';
import { footprintGround, terraceMinRise } from './terraces';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { groundMaterialAt } from './groundMaterial';
import { BUILD_SEAT } from './buildSeat';
import { structureFrame } from './hits';
import { vecNormalize } from '../math';
import type { SiteScore } from './openLayout';

/** A footprint to score a site for (people), and the yaw it would stand at (radians; absent: the frame's own). */
export interface SiteFootprint { halfXPeople: number; halfZPeople: number; yaw?: number }

/** The score's weights: a person of rise costs `rise`; a person³ of earthwork (people² of pad × people of height) `earthwork`; a refused cut `refusal`. */
export const SITE_SCORE = { rise: 1, earthwork: 0.02, refusal: 100, /** rock and ice refuse a cut deeper than this (people): the seat's blend, in the spirit of 67.52 */ hardCutPeople: BUILD_SEAT.rockBlendPeople * 10 } as const;

export interface SiteReport {
  /** The natural rise under the footprint (people). */
  rise: number;
  /** The pad's level a terrace would take (a radius, world), the samples' median. */
  level: number;
  /** Σ |level − natural| over the footprint (people³: each sample stands for its share of the pad's area). */
  earthwork: number;
  /** The hardest ground under the footprint, and whether a terrace would cut it deeper than hard ground allows. */
  material: GroundMaterial;
  refused: boolean;
  /** Whether a terrace would be laid (the rise in the terrace's window), none needed (under the least rise), or the site splits (over the cap). */
  terrace: 'none' | 'one' | 'split';
  /** The pads the site would split into when over the cap: along the contour, each half the footprint across the slope, at its own median level (people, the frame; world levels). */
  pads: Array<{ x: number; z: number; halfX: number; halfZ: number; level: number }>;
  score: number;
}

const HARD: ReadonlySet<GroundMaterial> = new Set<GroundMaterial>(['rock', 'ice']);

/** The site read for a footprint at `dir` (a unit direction) on a planet: see SiteReport. */
export function siteReport(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, dir: Vec3, footprint: SiteFootprint): SiteReport {
  const planet = state.planets[planetIndex], ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const up = vecNormalize(dir), { tz } = structureFrame(footprint.yaw !== undefined ? { yaw: footprint.yaw } : undefined, up);
  const across = { x: tz.y * up.z - tz.z * up.y, y: tz.z * up.x - tz.x * up.z, z: tz.x * up.y - tz.y * up.x };
  const halfX = footprint.halfXPeople * person, halfZ = footprint.halfZPeople * person;
  const g = footprintGround(planet, up, tz, across, halfX, halfZ, ps);
  const rise = g.rise / person;
  const area = (2 * footprint.halfXPeople) * (2 * footprint.halfZPeople) / g.samples.length;   // people² a sample
  let earthwork = 0;
  for (const h of g.samples) earthwork += (Math.abs(h - g.level) / person) * area;
  // the material: the hardest of the centre and the four corners; a cut into it deeper than the blend is refused
  let material: GroundMaterial = groundMaterialAt(planet, up), hard = HARD.has(material);
  for (const [sx, sz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
    const d = vecNormalize({ x: up.x + (across.x * sx * halfX + tz.x * sz * halfZ) / planet.radius, y: up.y + (across.y * sx * halfX + tz.y * sz * halfZ) / planet.radius, z: up.z + (across.z * sx * halfX + tz.z * sz * halfZ) / planet.radius });
    const m = groundMaterialAt(planet, d);
    if (HARD.has(m) && !hard) { material = m; hard = true; }
  }
  const crest = Math.max(...g.samples);
  const minRise = terraceMinRise(state.config);
  const refused = hard && rise >= minRise && crest - g.level > SITE_SCORE.hardCutPeople * person;
  const terrace: SiteReport['terrace'] = rise < minRise ? 'none' : rise <= TERRACE.maxStepPeople ? 'one' : 'split';
  // the split: along the contour — the slope's direction from the samples' corners; two pads halved across it
  const pads: SiteReport['pads'] = [];
  if (terrace === 'split') {
    const n = TERRACE.samples, at = (i: number, j: number) => g.samples[i * n + j];
    const dX = (at(n - 1, 0) + at(n - 1, n - 1)) - (at(0, 0) + at(0, n - 1)), dZ = (at(0, n - 1) + at(n - 1, n - 1)) - (at(0, 0) + at(n - 1, 0));
    const alongX = Math.abs(dX) >= Math.abs(dZ);   // the slope runs along x: the pads stand side by side across x
    for (const side of [-1, 1]) {
      const picked: number[] = [];
      for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) { const c = (alongX ? i : j) - (n - 1) / 2; if (Math.sign(c) === side || c === 0) picked.push(at(i, j)); }
      picked.sort((a, b) => a - b);
      const m = picked.length >> 1, level = picked.length % 2 ? picked[m] : (picked[m - 1] + picked[m]) / 2;
      pads.push(alongX ? { x: side * footprint.halfXPeople / 2, z: 0, halfX: footprint.halfXPeople / 2, halfZ: footprint.halfZPeople, level } : { x: 0, z: side * footprint.halfZPeople / 2, halfX: footprint.halfXPeople, halfZ: footprint.halfZPeople / 2, level });
    }
  }
  const score = -(SITE_SCORE.rise * rise + SITE_SCORE.earthwork * earthwork + (refused ? SITE_SCORE.refusal : 0));
  return { rise, level: g.level, earthwork, material, refused, terrace, pads, score };
}

/**
 * The site score for a footprint, higher the better — lane C's `SiteScore` (core/openLayout.ts, step A's open board:
 * `placeOpenObjectives(state, score)` takes it as it is), reading only the planets and the config so the settlements' scan
 * can hand it the planet as generated (tests/settlements.test.ts holds the assignment).
 */
export function terraceSiteScore(footprint: SiteFootprint): (state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, dir: Vec3) => number {
  return (state, planetIndex, dir) => siteReport(state, planetIndex, dir, footprint).score;
}
/** (The shape lane C's layout pass takes, held by the type: a SiteScore is this, given a footprint.) */
export const asSiteScore = (footprint: SiteFootprint): SiteScore => terraceSiteScore(footprint);
